import { useCallback, useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FundingProfileData, FundingTypes } from '@Types';
import { useSetFieldErrors } from '@Hooks';
import { useUpdateFunderProfile } from '@Hooks/Api';
import { Loader } from '@Components';
import { useNotificationsContext } from '@Contexts';
import { useRouter } from '@Helpers';
import isServer from '@Helpers/isServer';
import { RoutingPaths } from '@App/paths';

import { useFormData } from './useFormData';
import { Profile } from '../Profile';
import { useProfileTypes } from '../useProfileTypes';
import { setBackendFieldErrors } from '@Helpers/setBackendFieldErrors';

const TYPES = Object.keys(FundingTypes);

export const FunderProfileController = ({ forceNoProfile = false, organizationName = '' }) => {
  const { t } = useTranslation('createAccount');
  const methods = useForm<FundingProfileData>();
  const { setError, reset } = methods;
  const [{ data: dataFunder, loading: loadingFunder, isError: noFunder }] = useFormData(
    forceNoProfile,
    organizationName,
  );
  const [updateFunderProfile, response] = useUpdateFunderProfile(noFunder);
  const { error, loading: isSaving } = response;
  const { push, params } = useRouter();
  const typeList = useProfileTypes('funding_types', TYPES);
  const { error: errorNotification } = useNotificationsContext();
  const nextUrl = params.next as string | undefined;

  useEffect(() => {
    if (!loadingFunder && dataFunder) {
      reset(dataFunder);
    }
  }, [reset, loadingFunder, dataFunder]);

  const onSubmit = useCallback(
    (data: FundingProfileData) => {
      const formattedData = {
        ...data,
        company_number: data.company_number || undefined,
        charity_number: data.charity_number || undefined,
        other_registration_number: data.other_registration_number || undefined,
      };
      updateFunderProfile(formattedData)
        .then(() => {
          if (noFunder && !isServer && window.dataLayer) {
            window.dataLayer.push({ event: 'funderCreateProfile' });
          }
          if (nextUrl) {
            push(nextUrl);
          } else {
            push(RoutingPaths.DASHBOARD_FUNDER);
          }
        })
        .catch(({ data }) => {
          if (data?.field_errors) {
            setBackendFieldErrors(data.field_errors, setError);
          } else {
            errorNotification(t('common:errors.generic_error'));
          }
        });
    },
    [updateFunderProfile, noFunder, nextUrl, push, setError, errorNotification, t],
  );

  const onCancel = useCallback(() => {
    if (nextUrl) {
      push(nextUrl);
    } else {
      push(RoutingPaths.DASHBOARD_FUNDER);
    }
  }, [push, nextUrl]);

  useSetFieldErrors({
    fieldErrors: error?.field_errors,
    setError,
    loading: loadingFunder,
  });

  if (!forceNoProfile && (loadingFunder || !dataFunder)) return <Loader />;

  return (
    <FormProvider {...methods}>
      <Profile
        isFunder={true}
        onSubmit={methods.handleSubmit(onSubmit)}
        onCancel={onCancel}
        isSaving={isSaving}
        typeList={typeList}
        createMode={noFunder}
        headerDisclaimer={t('funder_profile.header_disclaimer')}
        focusAreaDisclaimer={t('funder_profile.focus_areas_disclaimer')}
      />
    </FormProvider>
  );
};
